/**
 * Tidy Router
 * 
 * Smart routing based on authentication status:
 * - Logged out → Landing page (replaces app content)
 * - Logged in → Main app (reveals existing app HTML)
 * 
 * Uses dynamic imports for code splitting
 */

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Check if user is authenticated by verifying session token
 */
async function checkAuth(): Promise<boolean> {
  // #region agent log
  console.log('[TIDY_DEBUG] checkAuth called', { fullUrl: window.location.href, pathname: window.location.pathname, search: window.location.search, API_BASE });
  // #endregion
  // First check if we have a token
  const token = localStorage.getItem('tidy_session');
  
  // Check URL params for OAuth callback (coming back from Google)
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  const urlCsrf = params.get('csrf');
  
  // #region agent log
  console.log('[TIDY_DEBUG] Token state', { hasStoredToken: !!token, hasUrlToken: !!urlToken, hasUrlCsrf: !!urlCsrf, urlTokenPreview: urlToken?.substring(0, 10) });
  // #endregion
  
  if (urlToken && urlCsrf) {
    // #region agent log
    console.log('[TIDY_DEBUG] OAuth callback detected, storing tokens', { urlTokenPreview: urlToken.substring(0, 10) + '...', pathBeforeClean: window.location.pathname });
    // #endregion
    // Store tokens from OAuth callback
    localStorage.setItem('tidy_session', urlToken);
    localStorage.setItem('tidy_csrf', urlCsrf);
    // Clean URL
    window.history.replaceState({}, '', window.location.pathname);
    return true;
  }
  
  if (!token) {
    // #region agent log
    console.log('[TIDY_DEBUG] No token found, returning false');
    // #endregion
    return false;
  }
  
  // Verify token is still valid by calling session endpoint
  // #region agent log
  console.log('[TIDY_DEBUG] Verifying token with API', { API_BASE, tokenPreview: token?.substring(0, 10) });
  // #endregion
  try {
    const response = await fetch(`${API_BASE}/v1/auth/session`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    // #region agent log
    console.log('[TIDY_DEBUG] Session API response', { ok: response.ok, status: response.status });
    // #endregion
    
    if (response.ok) {
      const data = await response.json();
      // #region agent log
      console.log('[TIDY_DEBUG] Session valid, got CSRF token', { hasCsrf: !!data.data?.csrfToken });
      // #endregion
      // Update CSRF token
      if (data.data?.csrfToken) {
        localStorage.setItem('tidy_csrf', data.data.csrfToken);
      }
      return true;
    }
    
    // #region agent log
    console.log('[TIDY_DEBUG] Token invalid, clearing auth');
    // #endregion
    // Token invalid - clear it
    localStorage.removeItem('tidy_session');
    localStorage.removeItem('tidy_csrf');
    return false;
  } catch (error) {
    // #region agent log
    console.log('[TIDY_DEBUG] Session API error', { error: String(error) });
    // #endregion
    // Network error - if we have a token, assume it might still be valid
    // and let the main app handle re-auth if needed
    console.warn('Auth check network error, proceeding with cached token');
    return !!token;
  }
}

/**
 * Load and initialize the landing page
 * Completely replaces the #app content
 */
async function loadLanding(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;
  
  // Import and initialize landing page
  const { initLanding } = await import('./landing/Landing');
  
  // Clear app content and remove loading state before init
  app.innerHTML = '';
  app.classList.remove('loading-router');
  
  // Initialize landing page
  initLanding();
}

/**
 * Load and initialize the main app
 * Reveals the existing app HTML structure
 */
async function loadApp(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;
  
  // Remove loading state to reveal app HTML
  app.classList.remove('loading-router');
  
  // Remove the router loading spinner
  const loadingSpinner = app.querySelector('.router-loading');
  if (loadingSpinner) {
    loadingSpinner.remove();
  }
  
  // Import and run the main app
  await import('./main');
}

/**
 * Main router entry point
 */
async function init(): Promise<void> {
  // #region agent log
  console.log('[TIDY_DEBUG] Router init started', { href: window.location.href, pathname: window.location.pathname });
  // #endregion
  try {
    const isAuthenticated = await checkAuth();
    
    // #region agent log
    console.log('[TIDY_DEBUG] Auth check completed', { isAuthenticated });
    // #endregion
    
    if (isAuthenticated) {
      // #region agent log
      console.log('[TIDY_DEBUG] Loading main app');
      // #endregion
      // User is logged in - load the main app
      await loadApp();
    } else {
      // #region agent log
      console.log('[TIDY_DEBUG] Loading landing page');
      // #endregion
      // User is logged out - show landing page
      await loadLanding();
    }
  } catch (error) {
    console.error('Router initialization error:', error);
    // #region agent log
    console.log('[TIDY_DEBUG] Router init error', { error: String(error) });
    // #endregion
    // On error, try to show landing page as fallback
    await loadLanding();
  }
}

// Start the router when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
